import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { SITE, buildWhatsAppLink } from "@/data/site";
import { useLang, u } from "@/data/i18n";

const LINKS = [
  { key: "nav_products", href: "#products" },
  { key: "nav_legacy", href: "#legacy" },
  { key: "nav_presence", href: "#presence" },
  { key: "nav_manufacturing", href: "#manufacturing" },
  { key: "nav_contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.__lenis?.scrollTo(href, { offset: -70 }), 200);
    } else if (window.__lenis) {
      window.__lenis.scrollTo(href, { offset: -70 });
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-md border-b border-ink/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto flex items-center justify-between px-5 md:px-10 py-3 md:py-4">
        <a href="#top" onClick={(e) => go(e, "#top")} className="flex items-center gap-3" data-testid="header-logo">
          <img
            src={`${process.env.PUBLIC_URL}/assets/logo-transparent.png`}
            alt="Lovely Foodpacks logo"
            className="h-14 md:h-16 w-auto [filter:drop-shadow(0_0_6px_rgba(255,255,255,0.95))_drop-shadow(0_0_16px_rgba(255,255,255,0.65))_drop-shadow(0_0_28px_rgba(250,204,21,0.35))]"
          />
          <span className={`hidden sm:block leading-tight ${scrolled ? "text-leaf" : "text-white"}`}>
            <span className="block font-display font-800 font-extrabold tracking-tight text-sm md:text-base">Lovely Foodpacks</span>
            <span className={`block text-[10px] tracking-[0.2em] uppercase ${scrolled ? "text-muted" : "text-white/60"}`}>
              {u(lang, "since_badge")}
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="header-nav">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-${l.key.replace("nav_", "")}`}
              className={`text-sm font-semibold tracking-wide transition-colors duration-300 hover:text-leaf-bright ${
                scrolled ? "text-ink" : "text-white/85"
              }`}
            >
              {u(lang, l.key)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "en" ? "ta" : "en")}
            className={`inline-flex items-center gap-1.5 border px-3.5 py-2 text-xs font-bold tracking-widest uppercase transition-colors duration-300 ${
              scrolled
                ? "border-leaf/30 text-leaf hover:bg-leaf hover:text-white"
                : "border-white/30 text-white hover:border-sun hover:text-sun"
            }`}
            aria-label="Toggle Tamil / English"
          >
            <Languages size={14} />
            {lang === "en" ? "தமிழ்" : "EN"}
          </button>
          <a
            data-testid="header-enquire-btn"
            href={buildWhatsAppLink("Hello Lovely Foodpacks! I'd like to enquire about your products.")}
            target="_blank"
            rel="noreferrer"
            className="group hidden sm:inline-flex items-center gap-2 bg-leaf text-white px-5 py-2.5 text-sm font-bold tracking-wide transition-colors duration-300 hover:bg-sun hover:text-leaf"
          >
            {u(lang, "enquire_now")}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 ${scrolled ? "text-ink" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-ink/10 px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={(e) => go(e, l.href)} className="text-lg font-display font-bold text-leaf">
              {u(lang, l.key)}
            </a>
          ))}
          <a
            data-testid="mobile-enquire-btn"
            href={buildWhatsAppLink("Hello Lovely Foodpacks! I'd like to enquire about your products.")}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center justify-center gap-2 bg-leaf text-white px-5 py-3 font-bold"
          >
            {u(lang, "enquire_now")} <ArrowUpRight size={16} />
          </a>
        </div>
      )}
    </header>
  );
}
